import type { Course } from '../types';

export const CALENDAR_TIME_ZONE = 'Asia/Tokyo';
export const ACADEMIC_CALENDAR_URL = 'https://www.tufs.ac.jp/student/calendar/';

// TUFS class hours. Period 6 is 120 minutes, not 90 minutes.
// https://www.tufs.ac.jp/documents/admission/international_applicant/2025_spring_course_j.pdf
export const CLASS_PERIOD_TIMES: Record<number, { start: string; end: string }> = {
  1: { start: '08:30', end: '10:00' },
  2: { start: '10:10', end: '11:40' },
  3: { start: '12:40', end: '14:10' },
  4: { start: '14:20', end: '15:50' },
  5: { start: '16:00', end: '17:30' },
  6: { start: '17:40', end: '19:40' },
};

export type CalendarRange = { startDate: string; endDate: string };
export type CalendarValidationCode = 'invalidDate' | 'reversedRange' | 'longRange' | 'missingName' | 'noWeeklySlot' | 'noOccurrence';
export class CalendarValidationError extends Error {
  constructor(public readonly code: CalendarValidationCode) { super(code); }
}

// Verified teaching dates, not exam dates. No fabricated defaults for other years.
export const CALENDAR_PRESETS = {
  spring2026: { startDate: '2026-04-08', endDate: '2026-07-28' },
  autumn2026: { startDate: '2026-09-28', endDate: '2027-01-29' },
} satisfies Record<string, CalendarRange>;

const DAY_MS = 86400000;
const WEEK_MS = 7 * DAY_MS;
const JST_OFFSET_MS = 9 * 3600000;
const GOOGLE_WEEKDAYS = ['', 'MO', 'TU', 'WE', 'TH', 'FR', 'SA', 'SU'];

/** Slot identity is deliberately independent of Firebase and never mutates course data. */
export function calendarCourseKey(course: Course): string {
  return JSON.stringify([course.academicYear ?? '', course.semester ?? '', course.id, course.dayOfWeek ?? null, course.period ?? null]);
}

export function getDefaultCalendarRange(courses: readonly Course[], now = new Date()): CalendarRange {
  const academicCourses = courses.filter(course => !course.isCustom);
  if (academicCourses.length) {
    const terms = new Set(academicCourses.map(course => {
      const year = String(course.academicYear ?? '').match(/\d{4}/)?.[0];
      const semester = (course.semester ?? '').replace(/学期$/, '');
      return year === '2026' && (semester === '春' || semester === '秋') ? semester : '';
    }));
    if (terms.size === 1 && terms.has('春')) return { ...CALENDAR_PRESETS.spring2026 };
    if (terms.size === 1 && terms.has('秋')) return { ...CALENDAR_PRESETS.autumn2026 };
    return { startDate: '', endDate: '' };
  }
  const today = new Date(now.getTime() + JST_OFFSET_MS).toISOString().slice(0, 10);
  const active = Object.values(CALENDAR_PRESETS).find(range => today >= range.startDate && today <= range.endDate);
  return active ? { ...active } : { startDate: '', endDate: '' };
}

export function parseCalendarDate(value: string): number {
  if (!/^\d{4}-\d{2}-\d{2}$/.test(value) || Number(value.slice(0, 4)) < 1900) {
    throw new CalendarValidationError('invalidDate');
  }
  const date = new Date(`${value}T00:00:00Z`);
  if (!Number.isFinite(date.getTime()) || date.toISOString().slice(0, 10) !== value) {
    throw new CalendarValidationError('invalidDate');
  }
  return date.getTime();
}

export function validateCalendarRange(range: CalendarRange): { start: number; end: number } {
  const start = parseCalendarDate(range.startDate);
  const end = parseCalendarDate(range.endDate);
  if (end < start) throw new CalendarValidationError('reversedRange');
  if (end - start > 366 * DAY_MS) throw new CalendarValidationError('longRange');
  return { start, end };
}

export function getCourseCalendarIssue(course: Course): CalendarValidationCode | null {
  if (!course.name?.trim()) return 'missingName';
  const text = [course.name, course.room, course.time, course.semester].join(' ').normalize('NFKC');
  if (!Number.isInteger(course.dayOfWeek) || course.dayOfWeek < 1 || course.dayOfWeek > 7 ||
      !Number.isInteger(course.period) || !CLASS_PERIOD_TIMES[course.period] ||
      /オンデマンド|on[ -]?demand|集中講義/i.test(text)) return 'noWeeklySlot';
  return null;
}

function classTimeUtc(day: number, time: string): number {
  const [hours, minutes] = time.split(':').map(Number);
  return day + (hours * 60 + minutes) * 60000 - JST_OFFSET_MS;
}

const dateString = (day: number) => new Date(day).toISOString().slice(0, 10);
export const calendarUtcTimestamp = (time: number) => new Date(time).toISOString().replace(/[-:]/g, '').replace(/\.\d{3}Z$/, 'Z');

export type CalendarSeries = {
  key: string;
  course: Course;
  firstDate: string;
  lastDate: string;
  occurrences: number;
  startUtc: number;
  endUtc: number;
  recurrence: string;
};

export function buildCalendarSeries(course: Course, range: CalendarRange): CalendarSeries {
  const { start, end } = validateCalendarRange(range);
  const issue = getCourseCalendarIssue(course);
  if (issue) throw new CalendarValidationError(issue);
  const dayOfWeek = course.dayOfWeek % 7; // Saved courses: Monday = 1, Sunday = 7.
  const first = start + ((dayOfWeek - new Date(start).getUTCDay() + 7) % 7) * DAY_MS;
  if (first > end) throw new CalendarValidationError('noOccurrence');
  const occurrences = Math.floor((end - first) / WEEK_MS) + 1;
  const last = first + (occurrences - 1) * WEEK_MS;
  const hours = CLASS_PERIOD_TIMES[course.period];
  return {
    key: calendarCourseKey(course), course,
    firstDate: dateString(first), lastDate: dateString(last), occurrences,
    startUtc: classTimeUtc(first, hours.start), endUtc: classTimeUtc(first, hours.end),
    // No BYDAY: the UTC weekday of an 08:30 JST class is the previous day.
    // DTSTART's weekday is the default, so every 7 days stays correct in both UTC and JST.
    recurrence: `RRULE:FREQ=WEEKLY;UNTIL=${calendarUtcTimestamp(classTimeUtc(last, hours.start))}`,
  };
}

function calendarDescription(course: Course): string {
  // Do not export personal notes, attendance, grades, email or any auth identifier.
  const syllabus = /^https?:\/\/[^\s]+$/i.test(course.syllabusUrl ?? '') ? course.syllabusUrl : '';
  return [course.professor || course.instructor, syllabus].filter(Boolean).join('\n');
}

/** Opens a draft only. The user must review and save it in Google Calendar. */
export function buildGoogleCalendarUrl(series: CalendarSeries): string {
  const hours = CLASS_PERIOD_TIMES[series.course.period];
  const localDate = series.firstDate.replace(/-/g, '');
  // Google template UI ignored the UTC UNTIL rule in browser verification.
  // Use local JST times and a bounded COUNT with an explicit local weekday instead.
  // ICS remains RFC 5545 UTC + UNTIL, independent of this UI-link format.
  const values: Record<string, string> = {
    action: 'TEMPLATE', text: series.course.name,
    dates: `${localDate}T${hours.start.replace(':', '')}00/${localDate}T${hours.end.replace(':', '')}00`,
    ctz: CALENDAR_TIME_ZONE,
    recur: `RRULE:FREQ=WEEKLY;INTERVAL=1;BYDAY=${GOOGLE_WEEKDAYS[series.course.dayOfWeek]};COUNT=${series.occurrences}`,
    location: series.course.room ?? '', details: calendarDescription(series.course),
  };
  const query = Object.entries(values).map(([key, value]) => `${key}=${encodeURIComponent(value)}`).join('&');
  return `https://calendar.google.com/calendar/render?${query}`;
}

function escapeCalendarText(value: string): string {
  return value.replace(/\\/g, '\\\\').replace(/\r\n|\r|\n/g, '\\n').replace(/;/g, '\\;').replace(/,/g, '\\,');
}

// RFC 5545: fold by UTF-8 octets, never split a Japanese character or emoji.
function foldCalendarLine(line: string): string {
  let result = '';
  let bytes = 0;
  for (const char of line) {
    const code = char.codePointAt(0);
    const width = code <= 0x7f ? 1 : code <= 0x7ff ? 2 : code <= 0xffff ? 3 : 4;
    if (bytes + width > 75) { result += '\r\n '; bytes = 1; }
    result += char;
    bytes += width;
  }
  return result;
}

/** An offline, deterministic-UID export; this is not live calendar synchronization. */
export function buildCalendarIcs(series: readonly CalendarSeries[], now = new Date()): string {
  if (!series.length) throw new Error('No calendar events selected');
  const lines = ['BEGIN:VCALENDAR', 'VERSION:2.0', 'PRODID:-//TUFS SYLA//Timetable//JA', 'CALSCALE:GREGORIAN', 'X-WR-CALNAME:TUFS SYLA', `X-WR-TIMEZONE:${CALENDAR_TIME_ZONE}`];
  const keys = new Set<string>();
  for (const event of series) {
    const uid = `${encodeURIComponent(event.key)}-${event.firstDate}-${event.lastDate}@tufs-syla.com`;
    if (keys.has(uid)) continue;
    keys.add(uid);
    lines.push('BEGIN:VEVENT', `UID:${uid}`, `DTSTAMP:${calendarUtcTimestamp(now.getTime())}`,
      `DTSTART:${calendarUtcTimestamp(event.startUtc)}`, `DTEND:${calendarUtcTimestamp(event.endUtc)}`,
      event.recurrence, `SUMMARY:${escapeCalendarText(event.course.name)}`,
      `LOCATION:${escapeCalendarText(event.course.room ?? '')}`,
      `DESCRIPTION:${escapeCalendarText(calendarDescription(event.course))}`,
      'CLASS:PRIVATE', 'END:VEVENT');
  }
  lines.push('END:VCALENDAR');
  return lines.map(foldCalendarLine).join('\r\n') + '\r\n';
}
